// Sdílené UI prvky AOP vrstev. Dřív si každá vrstva psala vlastní tlačítka,
// table shell, spinner i modal — rozcházely se v barvě, velikosti i popiskách.
import { useRef, useState, useEffect, useCallback } from 'react';
import { Loader2, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import { ACCENT } from '../../lib/uiTokens';

/** Načítací spinner vrstvy — jednotná barva i velikost. */
export function LayerSpinner({ size = 24 }) {
    return (
        <div className="flex items-center justify-center py-20">
            <Loader2 size={size} className="animate-spin" style={{ color: ACCENT }} />
        </div>
    );
}

/** Toolbar nad tabulkou — jednotná mezera i zalamování. */
export function Toolbar({ children }) {
    return <div className="flex items-center gap-2 mb-4 flex-wrap">{children}</div>;
}

/** Sekundární (neutrální) tlačítko. */
export function BtnSecondary({ onClick, disabled, title, icon: Icon, children }) {
    return (
        <button onClick={onClick} disabled={disabled} title={title}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-medium"
            style={{
                background: 'var(--surface-raised)',
                border: '1px solid var(--border-default)',
                color: 'var(--text-primary)',
                opacity: disabled ? 0.5 : 1,
            }}>
            {Icon && <Icon size={14} />}{children}
        </button>
    );
}

/**
 * Primární „Uložit" tlačítko. Sjednocuje popisek i pravidlo zakázání —
 * dřív jedna vrstva psala „Uložit změny (n)", jiná „Uložit (n)", a Materiál
 * šel uložit i bez rozdělaných změn.
 */
export function BtnUlozit({ onClick, saving, dirtyCount = 0, label = 'Uložit' }) {
    const disabled = saving || dirtyCount === 0;
    return (
        <button onClick={onClick} disabled={disabled}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ background: ACCENT, opacity: disabled ? 0.5 : 1 }}>
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Ukládám…' : `${label}${dirtyCount ? ` (${dirtyCount})` : ''}`}
        </button>
    );
}

/**
 * Obal tabulky vrstvy: karta + vodorovný scroll + pevný layout se sloupci
 * na `max-content`, aby se měsíční osa nesmršťovala. Kopírovalo se 6×.
 *
 * Na užších displejích se 12 měsíců nevejde a hledat posuvník je otrava, proto:
 *  – tažení myší kamkoli po tabulce (mimo vstupy) posouvá do boku,
 *  – na krajích vyjedou šipky, když je kam scrollovat,
 *  – okraj se zjemní přechodem, aby bylo poznat, že obsah pokračuje.
 * Dotyk řeší prohlížeč nativně.
 */
export function AopTable({ cardStyle, children }) {
    const ref = useRef(null);
    const [okraje, setOkraje] = useState({ vlevo: false, vpravo: false });
    const [tahne, setTahne] = useState(false);
    const drag = useRef(null);

    const prepocti = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const rezerva = 2; // sub-pixelové zaokrouhlení
        setOkraje({
            vlevo: el.scrollLeft > rezerva,
            vpravo: el.scrollLeft + el.clientWidth < el.scrollWidth - rezerva,
        });
    }, []);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        prepocti();
        el.addEventListener('scroll', prepocti, { passive: true });
        const ro = new ResizeObserver(prepocti);
        ro.observe(el);
        return () => { el.removeEventListener('scroll', prepocti); ro.disconnect(); };
    }, [prepocti, children]);

    // Tažením se nesmí rozbít psaní do buněk ani označování textu
    const jeInteraktivni = (t) => t.closest('input, select, textarea, button, a, [contenteditable]');

    const onPointerDown = (e) => {
        const el = ref.current;
        if (!el || e.button !== 0 || jeInteraktivni(e.target)) return;
        drag.current = { x: e.clientX, scroll: el.scrollLeft, aktivni: false };
    };
    const onPointerMove = (e) => {
        const el = ref.current;
        if (!el || !drag.current) return;
        const dx = e.clientX - drag.current.x;
        if (!drag.current.aktivni) {
            if (Math.abs(dx) < 5) return;      // práh, ať projde klik i výběr textu
            drag.current.aktivni = true;
            setTahne(true);
            el.setPointerCapture?.(e.pointerId);
        }
        el.scrollLeft = drag.current.scroll - dx;
    };
    const konecTazeni = (e) => {
        const el = ref.current;
        if (el && drag.current?.aktivni) el.releasePointerCapture?.(e.pointerId);
        drag.current = null;
        setTahne(false);
    };

    const posun = (smer) => {
        const el = ref.current;
        if (el) el.scrollBy({ left: smer * el.clientWidth * 0.6, behavior: 'smooth' });
    };

    return (
        <div className="rounded-2xl overflow-hidden flex flex-col flex-1 min-h-0" style={{ ...cardStyle, position: 'relative' }}>
            <div ref={ref} className="overflow-auto flex-1 min-h-0"
                style={{ cursor: tahne ? 'grabbing' : undefined, userSelect: tahne ? 'none' : undefined }}
                onPointerDown={onPointerDown} onPointerMove={onPointerMove}
                onPointerUp={konecTazeni} onPointerCancel={konecTazeni}>
                <table className="text-sm border-collapse"
                    style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
                    {children}
                </table>
            </div>
            <OkrajovyStin strana="left" viditelny={okraje.vlevo} />
            <OkrajovyStin strana="right" viditelny={okraje.vpravo} />
            <SipkaScroll strana="left" viditelna={okraje.vlevo} onClick={() => posun(-1)} />
            <SipkaScroll strana="right" viditelna={okraje.vpravo} onClick={() => posun(1)} />
        </div>
    );
}

/** Jemný přechod na kraji — signalizuje, že tabulka pokračuje. */
function OkrajovyStin({ strana, viditelny }) {
    if (!viditelny) return null;
    return (
        <div aria-hidden="true" style={{
            position: 'absolute', top: 0, bottom: 0, [strana]: 0, width: 28, pointerEvents: 'none',
            background: `linear-gradient(to ${strana === 'left' ? 'right' : 'left'}, rgba(0,0,0,0.16), transparent)`,
        }} />
    );
}

/** Kruhová šipka pro posun o kus tabulky. Zobrazí se, jen když je kam jet. */
function SipkaScroll({ strana, viditelna, onClick }) {
    if (!viditelna) return null;
    const Ikona = strana === 'left' ? ChevronLeft : ChevronRight;
    return (
        <button onClick={onClick} tabIndex={-1}
            aria-label={strana === 'left' ? 'Posunout doleva' : 'Posunout doprava'}
            style={{
                position: 'absolute', [strana]: 6, top: '50%', transform: 'translateY(-50%)',
                width: 30, height: 30, borderRadius: '50%', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: 'var(--surface-overlay, var(--surface-raised))',
                border: '1px solid var(--border-strong)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.28)',
                color: 'var(--text-primary)', cursor: 'pointer', opacity: 0.9,
            }}>
            <Ikona size={17} />
        </button>
    );
}

/** Prázdný stav uvnitř tabulky — jednotná výška. */
export function EmptyRow({ colSpan, children }) {
    return (
        <tr>
            <td colSpan={colSpan} className="text-center py-10 text-sm" style={{ color: 'var(--text-tertiary)' }}>
                {children}
            </td>
        </tr>
    );
}

/** Modal se sjednoceným overlayem, rádiusem, pozadím i z-indexem. */
export function AopModal({ onClose, width = 560, isDark, children }) {
    return (
        <div onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 1100, display: 'flex',
                alignItems: 'center', justifyContent: 'center', padding: 16,
                background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
            }}>
            <div onClick={e => e.stopPropagation()}
                style={{
                    width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto',
                    borderRadius: 12, padding: 20,
                    background: isDark ? 'rgba(22,22,32,0.98)' : 'var(--surface-raised)',
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`,
                    boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
                }}>
                {children}
            </div>
        </div>
    );
}
